import "./index.css";
import { FloatingTree } from "@floating-ui/react";
import { useId, useMemo, useRef } from "react";
import { useControlledValue } from "../../hooks/useControlledValue";
import { useTranslations } from "../../i18n";
import { Slot } from "../../utils";
import { Dropdown } from "../Dropdown";
import { MenuContext } from "./context";
import { MenuContent } from "./MenuContent";
import type { IMenuProps } from "./types";
import { isMenuOption } from "./types";

export const Menu = (props: IMenuProps) => {
  const {
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    defaultIsOpen,
    trigger,
    children,
    options,
    onSelect,
    placement = "bottom-start",
    offset = 8,
    contentClassName,
    preventDefaultOnTrigger,
    stopPropagationOnTrigger,
    minHeight,
    maxHeight,
    minWidth,
    maxWidth,
    // Dropdown-specific props
    disabled,
    middleware,
    flip,
    shift,
    autoPlacement,
    clickOptions,
    hoverOptions,
    focusOptions,
    dismissOptions,
    roleOptions,
    listNavigationOptions: listNavigationOptionsProp,
    typeaheadOptions,
    floatingFocusManager: floatingFocusManagerProp,
    ariaLabel,
    triggerId: triggerIdProp,
    contentId: contentIdProp,
    matchTriggerWidth,
    typeaheadListRef: typeaheadListRefProp,
    // Remaining props forwarded to the trigger element
    ...forwardedProps
  } = props;

  const { rtl } = useTranslations();

  const { value: open, onChange: onOpenChange } = useControlledValue<boolean>({
    controlledValue: controlledOpen,
    controlledOnChange: controlledOnOpenChange,
    defaultValue: defaultIsOpen,
  });

  const generatedId = useId();
  const triggerId =
    children.props.id || triggerIdProp || `${generatedId}-trigger`;
  const contentId =
    contentIdProp ||
    (children.props.id
      ? `${children.props.id}-content`
      : `${generatedId}-content`);

  const typeaheadListRef = useRef<(string | null)[]>([]);
  useMemo(() => {
    typeaheadListRef.current = options
      .filter(isMenuOption)
      .map((option) => option.text);
  }, [options]);

  const triggerElementBase = trigger || children;

  const triggerElement = useMemo(() => {
    return <Slot {...forwardedProps}>{triggerElementBase}</Slot>;
  }, [forwardedProps, triggerElementBase]);

  return (
    <FloatingTree>
      <MenuContext.Provider value={{ onSelect, options, rtl }}>
        <Dropdown
          trigger={triggerElement}
          content={<MenuContent options={options} />}
          triggerId={triggerId}
          contentId={contentId}
          open={open}
          onOpenChange={onOpenChange}
          disabled={disabled}
          interactions={["click", "dismiss", "listNavigation", "typeahead"]}
          role="menu"
          placement={placement}
          offset={offset}
          middleware={middleware}
          flip={flip}
          shift={shift}
          autoPlacement={autoPlacement}
          clickOptions={clickOptions}
          hoverOptions={hoverOptions}
          focusOptions={focusOptions}
          dismissOptions={dismissOptions}
          roleOptions={roleOptions}
          typeaheadOptions={typeaheadOptions}
          ariaLabel={ariaLabel}
          matchTriggerWidth={matchTriggerWidth}
          floatingFocusManager={{
            initialFocus: -1,
            restoreFocus: false,
            ...floatingFocusManagerProp,
          }}
          contentClassName={contentClassName}
          listNavigationOptions={{
            parentOrientation: "vertical",
            rtl,
            loop: true,
            ...listNavigationOptionsProp,
          }}
          typeaheadListRef={typeaheadListRefProp ?? typeaheadListRef}
          preventDefaultOnTrigger={preventDefaultOnTrigger}
          stopPropagationOnTrigger={stopPropagationOnTrigger}
          minHeight={minHeight}
          maxHeight={maxHeight}
          minWidth={minWidth}
          maxWidth={maxWidth}
        />
      </MenuContext.Provider>
    </FloatingTree>
  );
};
